/**
 * Shared billing code identifier helpers.
 *
 * Keep these semantics aligned with the backend charges/code_id utility.
 *
 * Two tiers of strictness (GDN-2658):
 * - DISPLAY / lenient: `normalizeToCodeId` preserves legacy identifiers so the
 *   UI can keep rendering whatever was persisted (e.g. `CPT:CASH`, `NDC:...`).
 * - OUTBOUND WRITE / strict: `normalizeToCodeIdStrict` / `isValidCodeId`
 *   enforce the backend contract — `CPT:`/`HCPCS:` prefix plus a bare code
 *   matching ^[A-Z0-9]{5}$. Anything else must never be sent to the API.
 */

const HCPCS_PATTERN = /^[A-EGHJ-TVUX][0-9]{4}$/;
const SUPPORTED_CODE_SYSTEMS = new Set(['CPT', 'HCPCS']);

/**
 * Canonical constraint for a bare billing code (backend validates
 * `^[A-Z0-9]{5}$` after stripping the `CPT:`/`HCPCS:` prefix).
 */
export const BILLING_BARE_CODE_PATTERN = /^[A-Z0-9]{5}$/;

export const stripCodeSystem = (codeId: string): string => {
  const colonIndex = codeId.indexOf(':');
  return colonIndex !== -1 ? codeId.slice(colonIndex + 1) : codeId;
};

export const sanitizeBillingCodeInput = (value: string): string =>
  value
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 5);

export const normalizeToCodeId = (code: string): string => {
  const trimmed = (code ?? '').trim().toUpperCase();
  if (!trimmed) return '';

  const colonIndex = trimmed.indexOf(':');
  if (colonIndex !== -1) {
    const suppliedSystem = trimmed.slice(0, colonIndex);
    const suppliedCode = trimmed.slice(colonIndex + 1).trim();
    if (!suppliedCode) return '';
    // Preserve unsupported legacy identifiers during unrelated edits. New
    // picker input cannot create these prefixes, and callers can surface
    // explicit validation instead of silently rewriting the identifier.
    if (!SUPPORTED_CODE_SYSTEMS.has(suppliedSystem)) return `${suppliedSystem}:${suppliedCode}`;

    const bare = sanitizeBillingCodeInput(suppliedCode);
    if (!bare) return '';
    const system = suppliedSystem === 'CPT' && HCPCS_PATTERN.test(bare) ? 'HCPCS' : suppliedSystem;
    return `${system}:${bare}`;
  }

  const bare = sanitizeBillingCodeInput(trimmed);
  if (!bare) return '';
  return HCPCS_PATTERN.test(bare) ? `HCPCS:${bare}` : `CPT:${bare}`;
};

/**
 * Whether a bare (prefix-less) billing code conforms to the backend contract.
 */
export const isValidBareCode = (code: string): boolean =>
  BILLING_BARE_CODE_PATTERN.test((code ?? '').trim().toUpperCase());

/**
 * Whether a full `SYSTEM:CODE` identifier conforms to the backend write
 * contract: a supported code system (CPT/HCPCS) plus a 5-char alphanumeric
 * bare code. Legacy sentinels (`CPT:CASH`, `CPT:95`, `CPT:`) and unsupported
 * systems (`NDC:...`) fail this check and must never be sent in a write.
 */
export const isValidCodeId = (codeId: string): boolean => {
  const trimmed = (codeId ?? '').trim().toUpperCase();
  const colonIndex = trimmed.indexOf(':');
  if (colonIndex === -1) return false;
  const system = trimmed.slice(0, colonIndex);
  const bare = trimmed.slice(colonIndex + 1);
  return SUPPORTED_CODE_SYSTEMS.has(system) && BILLING_BARE_CODE_PATTERN.test(bare);
};

/**
 * Strict variant of `normalizeToCodeId` for OUTBOUND WRITE payloads.
 *
 * Returns the canonical `SYSTEM:CODE` identifier, or `null` when the input
 * cannot be represented as a conforming code — too short (`95`), too long
 * (`992130` is NOT truncated here, unlike the lenient sanitize), an
 * unsupported system prefix, or empty. Callers must treat `null` as
 * "do not send", not as "send empty".
 */
export const normalizeToCodeIdStrict = (code: string): string | null => {
  const trimmed = (code ?? '').trim();
  if (!trimmed) return null;
  const colonIndex = trimmed.indexOf(':');
  const rawBare = colonIndex !== -1 ? trimmed.slice(colonIndex + 1) : trimmed;
  const cleanedBare = rawBare.toUpperCase().replace(/[^A-Z0-9]/g, '');
  // Validate on the un-truncated bare code so over-long input is rejected
  // instead of being silently clipped to five characters.
  if (!BILLING_BARE_CODE_PATTERN.test(cleanedBare)) return null;
  const normalized = normalizeToCodeId(trimmed);
  return normalized && isValidCodeId(normalized) ? normalized : null;
};

/**
 * Whether a bare code is shaped like something that can actually bill.
 *
 * Stricter than `isValidBareCode`, which only enforces the backend's
 * `^[A-Z0-9]{5}$` storage contract and therefore accepts five letters:
 * `normalizeCptCode('psych')` returns `'PSYCH'`. `CPTCodePicker` is
 * `freeSolo` on purpose — an off-list payer-specific HCPCS code still has
 * to be enterable — so the picker cannot gate on its bundled list, and
 * without a shape check "free" meant any string (GDN-3084).
 *
 * The shapes that bill are narrow:
 *
 * - CPT Category I — five digits (`90837`)
 * - HCPCS Level II — a letter then four digits (`H0004`)
 * - CPT Category II/III — four digits then `F`, `T`, or `U` (`3074F`, `0512T`)
 *
 * Five letters is none of them. Use this to gate what a picker COMMITS.
 * Do not use it to gate what is DISPLAYED: values persisted before this
 * check existed must keep rendering, or an unrelated edit would silently
 * drop them (the same reasoning as `normalizeToCodeId` staying lenient).
 */
export const BILLABLE_CODE_SHAPE = /^(?:\d{5}|[A-Z]\d{4}|\d{4}[FTU])$/;

export const isBillableCodeShape = (raw: string | null | undefined): boolean =>
  typeof raw === 'string' && BILLABLE_CODE_SHAPE.test(raw.trim().toUpperCase());
